// ts-rest contract for the vulnerability module (PRD #74, Gap Analysis
// Module 6).
//
// Endpoints under /api/vulnerability/*:
//   GET  /api/vulnerability/dashboard          — firm-wide demographics
//   GET  /api/vulnerability/records            — list per-client records
//   GET  /api/vulnerability/records/:clientId  — single client record
//   GET  /api/vulnerability/evidence-library   — anonymised case studies
//   POST /api/vulnerability/flag/:id/confirm   — confirm a flag, queue HAVEN
//   POST /api/vulnerability/record/:clientId/consent — toggle consent flags
//
// The FCA four-driver taxonomy lives in
// vendor/septima-ontology-wealth/packages/ontology-wealth/src/vulnerability-taxonomy.ts. SENTINEL and
// HAVEN reference it; this contract mirrors the driver enum.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const vulnerabilityDriverSchema = z.enum([
    'health',
    'life_event',
    'resilience',
    'capability',
]);
export type VulnerabilityDriver = z.infer<typeof vulnerabilityDriverSchema>;

export const vulnerabilitySeveritySchema = z.enum(['low', 'medium', 'high']);
export type VulnerabilitySeverity = z.infer<typeof vulnerabilitySeveritySchema>;

// ---------------------------------------------------------------------------
// Flag / evidence / plan rows
// ---------------------------------------------------------------------------

export const vulnerabilityFlagDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    imName: z.string(),
    driver: vulnerabilityDriverSchema,
    subCategory: z.string(),
    severity: vulnerabilitySeveritySchema,
    reviewRoute: z.string(),
    status: z.enum(['New', 'InProgress', 'Resolved']),
    raisedAt: z.string(),
    // The detectorFlag mixin column is nullable (a flag may exist with no
    // originating finding — e.g. one raised directly rather than via a
    // detector), so the DTO is honestly nullable.
    originFindingId: z.string().nullable(),
});
export type VulnerabilityFlagDto = z.infer<typeof vulnerabilityFlagDtoSchema>;

export const vulnerabilityEvidenceDtoSchema = z.object({
    id: z.string(),
    vulnerabilityFlagId: z.string(),
    evidenceQuote: z.string(),
    sourceKind: z.string(),
    sourceRef: z.string().nullable(),
    evidenceTimestamp: z.string(),
    recordedAt: z.string(),
});
export type VulnerabilityEvidenceDto = z.infer<typeof vulnerabilityEvidenceDtoSchema>;

export const vulnerabilityAdaptationPlanDtoSchema = z.object({
    id: z.string(),
    planRef: z.string(),
    vulnerabilityFlagId: z.string(),
    variantKind: z.enum(['light_touch', 'full_safeguarding']),
    communicationCadence: z.string(),
    documentationStyle: z.string(),
    familyInvolvement: z.string(),
    decisionCheckpoints: z.string(),
    status: z.enum(['Active', 'Superseded', 'Withdrawn']),
    activatedAt: z.string(),
});
export type VulnerabilityAdaptationPlanDto = z.infer<typeof vulnerabilityAdaptationPlanDtoSchema>;

// ---------------------------------------------------------------------------
// Per-client record
// ---------------------------------------------------------------------------

export const vulnerabilityRecordDtoSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    imName: z.string(),
    screeningStatus: z.enum(['not_started', 'in_progress', 'completed']),
    screeningCompletedAt: z.string().nullable(),
    screeningOutcome: z.enum(['no_indicators', 'indicators_present']).nullable(),
    activeDriverCount: z.number().int(),
    consentToRecord: z.boolean(),
    consentRecordedAt: z.string().nullable(),
    disclosureToFamily: z.boolean(),
    trustedContactName: z.string().nullable(),
    trustedContactRelationship: z.string().nullable(),
    lastReviewedAt: z.string().nullable(),
    notes: z.string().nullable(),
    flags: z.array(vulnerabilityFlagDtoSchema),
    evidence: z.array(vulnerabilityEvidenceDtoSchema),
    adaptationPlans: z.array(vulnerabilityAdaptationPlanDtoSchema),
});
export type VulnerabilityRecordDto = z.infer<typeof vulnerabilityRecordDtoSchema>;

// ---------------------------------------------------------------------------
// Firm-wide dashboard
// ---------------------------------------------------------------------------

export const vulnerabilityDriverPrevalenceSchema = z.object({
    driver: vulnerabilityDriverSchema,
    clientCount: z.number().int(),
    flagCount: z.number().int(),
});
export type VulnerabilityDriverPrevalence = z.infer<typeof vulnerabilityDriverPrevalenceSchema>;

export const vulnerabilityDashboardSchema = z.object({
    totalFlaggedClients: z.number().int(),
    driverPrevalence: z.array(vulnerabilityDriverPrevalenceSchema),
    interventionsInFlight: z.number().int(),
    interventionsClosed: z.number().int(),
    adaptationPlansActive: z.number().int(),
    /** Share of resolved flags with an adaptation plan — 0..100. */
    adaptationEffectivenessPct: z.number(),
    severityBreakdown: z.array(
        z.object({ severity: vulnerabilitySeveritySchema, count: z.number().int() }),
    ),
    records: z.array(vulnerabilityRecordDtoSchema),
});
export type VulnerabilityDashboard = z.infer<typeof vulnerabilityDashboardSchema>;

// ---------------------------------------------------------------------------
// Evidence library
// ---------------------------------------------------------------------------

export const vulnerabilityCaseStudySchema = z.object({
    id: z.string(),
    reference: z.string(),
    title: z.string(),
    driver: vulnerabilityDriverSchema,
    subCategory: z.string(),
    situation: z.string(),
    identification: z.string(),
    adaptationApplied: z.string(),
    outcome: z.string(),
    effectiveness: z.enum(['effective', 'partially_effective', 'ongoing']),
    publishedAt: z.string(),
});
export type VulnerabilityCaseStudy = z.infer<typeof vulnerabilityCaseStudySchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

// The server records the acting user from the session. The body carries no actor.
export const confirmFlagRequestSchema = z.object({
    note: z.string().optional(),
});
export type ConfirmFlagRequest = z.infer<typeof confirmFlagRequestSchema>;

export const consentUpdateRequestSchema = z.object({
    consentToRecord: z.boolean().optional(),
    disclosureToFamily: z.boolean().optional(),
    trustedContactName: z.string().optional(),
    trustedContactRelationship: z.string().optional(),
});
export type ConsentUpdateRequest = z.infer<typeof consentUpdateRequestSchema>;

// Manually raise a vulnerability flag (DESK10): the IM supplies the driver,
// sub-category, severity and a free-text reason explaining why the client is
// considered vulnerable.
export const createFlagRequestSchema = z.object({
    clientId: z.string().min(1),
    driver: vulnerabilityDriverSchema,
    subCategory: z.string().min(1),
    severity: vulnerabilitySeveritySchema,
    reason: z.string().min(1),
});
export type CreateFlagRequest = z.infer<typeof createFlagRequestSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const vulnerabilityContract = c.router(
    {
        dashboard: {
            method: 'GET',
            path: '/vulnerability/dashboard',
            query: z.object({ imName: z.string().optional() }).optional(),
            responses: { 200: vulnerabilityDashboardSchema },
            summary: 'Firm-wide (or IM-scoped) vulnerability dashboard',
        },
        getRecord: {
            method: 'GET',
            path: '/vulnerability/records/:clientId',
            responses: {
                200: z.object({ record: vulnerabilityRecordDtoSchema }),
                404: errorBody,
            },
            summary: 'Get a single per-client vulnerability record',
        },
        evidenceLibrary: {
            method: 'GET',
            path: '/vulnerability/evidence-library',
            responses: {
                200: z.object({ caseStudies: z.array(vulnerabilityCaseStudySchema) }),
            },
            summary: 'Anonymised vulnerability case-study library',
        },
        createFlag: {
            method: 'POST',
            path: '/vulnerability/flag',
            body: createFlagRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true), id: z.string() }),
                400: errorBody,
            },
            summary: 'Manually raise a vulnerability flag for a client (IM / Compliance)',
        },
        confirmFlag: {
            method: 'POST',
            path: '/vulnerability/flag/:id/confirm',
            body: confirmFlagRequestSchema,
            responses: {
                200: z.object({
                    ok: z.boolean(),
                    flag: vulnerabilityFlagDtoSchema,
                    havenTaskId: z.string().nullable(),
                }),
                404: errorBody,
                409: errorBody,
            },
            summary: 'Confirm a vulnerability flag and queue a HAVEN adaptation plan',
        },
        updateConsent: {
            method: 'POST',
            path: '/vulnerability/record/:clientId/consent',
            body: consentUpdateRequestSchema,
            responses: {
                200: z.object({ ok: z.boolean(), record: vulnerabilityRecordDtoSchema }),
                404: errorBody,
            },
            summary: 'Toggle consent-to-record / disclosure-to-family flags',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
